import {
  Badge,
  Button,
  ChannelBadge,
  ChannelNotice,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  EmptyState,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Separator,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  Download,
  FileText,
  Printer,
  MessageSquare,
  Package,
  Scale,
  Truck,
} from "lucide-react";
import { useState } from "react";
import { CopyButton } from "../../components/copy-button";
import { channelCopy } from "../../lib/channel";
import { ClaimReturnButton } from "../../components/claim-return-button";
import { ClaimThread } from "../../components/claim-thread";
import { MessageThread } from "../../components/message-thread";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { replyVarsFromOrder } from "../../lib/reply-templates";
import { downloadAuthenticated } from "../../lib/download";
import {
  formatBRL,
  formatDate,
  formatDateTime,
  formatPercent,
} from "../../lib/format";
import {
  CLAIM_STATUS_LABELS,
  CLAIM_STATUS_VARIANTS,
  CLAIM_TYPE_LABELS,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_VARIANTS,
  SHIPPING_STATUS_LABELS,
} from "../../lib/order-status";

export const Route = createFileRoute("/_app/orders_/$id")({
  component: OrderDetailPage,
});

/** "4126...3482" reads better than a 44-digit wrap; the key is copied, not read. */
function truncateMiddle(value: string, edge = 8): string {
  if (value.length <= edge * 2 + 1) return value;
  return `${value.slice(0, edge)}…${value.slice(-edge)}`;
}

function OrderDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const orderQuery = useQuery({
    queryKey: ["orders", id],
    queryFn: async () => unwrap(await api.orders({ id }).get()),
  });

  if (orderQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const order =
    orderQuery.data && "order" in orderQuery.data
      ? orderQuery.data.order
      : null;
  const invoices = order && "invoices" in order ? order.invoices : [];

  if (!order) {
    return (
      <Card>
        <EmptyState
          icon={<Package />}
          title="Pedido não encontrado"
          description="Ele pode ter sido removido ou pertencer a outra organização."
          action={
            <Link
              to="/orders"
              search={{ page: 1 }}
              className={buttonVariants({ variant: "secondary" })}
            >
              Voltar aos pedidos
            </Link>
          }
        />
      </Card>
    );
  }

  const buyer = order.buyerName ?? order.buyerNickname;
  const copy = channelCopy(
    "accountProvider" in order ? order.accountProvider : "mercadolivre",
  );

  return (
    <>
      <PageHeader
        title={`Pedido #${order.externalOrderId}`}
        description={[buyer, formatDateTime(order.placedAt)].filter(Boolean).join(" · ")}
        actions={
          <div className="flex items-center gap-2">
            <ChannelBadge
              provider={
                "accountProvider" in order ? order.accountProvider : "mercadolivre"
              }
              nickname={order.accountNickname}
            />
            <Badge
              variant={ORDER_STATUS_VARIANTS[order.status] ?? "neutral"}
              dot
            >
              {ORDER_STATUS_LABELS[order.status] ?? order.status}
            </Badge>
            <Link
              to="/orders"
              search={{ page: 1 }}
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              <ArrowLeft />
              Pedidos
            </Link>
          </div>
        }
      />

      <div className="flex flex-col gap-3">
        <Card className="animate-rise">
          <CardHeader>
            <CardTitle>Itens</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Produto</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Qtd</TableHead>
                <TableHead className="text-right">Unitário</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="max-w-96">
                    {item.listingId ? (
                      <Link
                        to="/listings/$id"
                        params={{ id: item.listingId }}
                        className="font-medium text-ink hover:text-ember"
                      >
                        {item.title}
                      </Link>
                    ) : (
                      <span className="font-medium text-ink">{item.title}</span>
                    )}
                    <p className="font-mono text-2xs text-ink-muted">
                      {item.externalItemId}
                    </p>
                  </TableCell>
                  <TableCell className="font-mono text-2xs">
                    {item.sellerSku ?? "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {item.quantity}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatBRL(item.unitPrice)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-ink">
                    {formatBRL(Number(item.unitPrice) * item.quantity)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-baseline justify-between border-t border-line px-4 py-2.5">
            <span className="text-2xs text-ink-muted">
              Total do pedido
              {order.currency !== "BRL" && ` (${order.currency})`}
            </span>
            <span className="font-mono text-[0.8125rem] font-semibold text-ink">
              {formatBRL(order.totalAmount)}
            </span>
          </div>
        </Card>

        {"profit" in order && order.profit ? (
          <ProfitCard
            profit={order.profit}
            provider={
              "accountProvider" in order ? order.accountProvider : "mercadolivre"
            }
          />
        ) : null}

        {"messages" in order ? (
          <Card className="animate-rise [animation-delay:30ms]">
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <MessageSquare className="size-3.5 text-ink-muted" />
                Mensagens
              </CardTitle>
            </CardHeader>
            {order.messages.length > 0 ? (
              <CardContent>
                <MessageThread
                  orderId={order.id}
                  messages={order.messages}
                  writesEnabled={Boolean(
                    "writesEnabled" in order && order.writesEnabled,
                  )}
                  vars={replyVarsFromOrder(order)}
                  onSent={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ["orders", id],
                    });
                    void queryClient.invalidateQueries({
                      queryKey: ["messages"],
                    });
                  }}
                />
              </CardContent>
            ) : (
              <EmptyState
                icon={<MessageSquare />}
                title="Nenhuma mensagem neste pedido"
                description={copy.messagesEmpty}
                action={
                  <Link
                    to="/messages"
                    search={{ awaiting: "true" }}
                    className={buttonVariants({
                      variant: "secondary",
                      size: "sm",
                    })}
                  >
                    Ver caixa
                  </Link>
                }
              />
            )}
          </Card>
        ) : null}

        {"claims" in order && Array.isArray(order.claims) && order.claims.length > 0 ? (
          <Card className="animate-rise [animation-delay:35ms]">
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <Scale className="size-3.5 text-ink-muted" />
                Reclamações
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {order.claims.map((claim) => (
                <div key={claim.id} className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={CLAIM_STATUS_VARIANTS[claim.status] ?? "neutral"}
                      dot
                    >
                      {CLAIM_STATUS_LABELS[claim.status] ?? claim.status}
                    </Badge>
                    <span className="text-[0.8125rem] text-ink">
                      {CLAIM_TYPE_LABELS[claim.type] ?? claim.type}
                    </span>
                    {claim.hasReturn ? (
                      <span className="text-2xs text-ink-muted">
                        Devolução
                        {claim.returnStatus ? ` · ${claim.returnStatus}` : ""}
                        {" — ajuste o estoque quando o item voltar."}
                      </span>
                    ) : null}
                  </div>
                  <OrderClaimBody
                    claimId={claim.id}
                    canReply={claim.status === "opened"}
                    writesEnabled={Boolean(
                      "writesEnabled" in order && order.writesEnabled,
                    )}
                    onSent={() => {
                      void queryClient.invalidateQueries({
                        queryKey: ["orders", id],
                      });
                      void queryClient.invalidateQueries({
                        queryKey: ["claims"],
                      });
                    }}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}

        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
          <ShippingCard
            orderId={order.id}
            order={order}
          />
          <InvoiceCard
            orderId={order.id}
            invoices={invoices}
            provider={
              "accountProvider" in order ? order.accountProvider : "mercadolivre"
            }
          />
        </div>

        <p className="text-2xs text-ink-muted">
          Sincronizado em {formatDateTime(order.syncedAt)}
          {order.buyerNickname && order.buyerName
            ? ` · apelido do comprador: ${order.buyerNickname}`
            : ""}
        </p>
      </div>
    </>
  );
}

const INCOMPLETE_HINT: Record<string, string> = {
  fees: "Reimporte o pedido para trazer a taxa de venda do canal.",
  cogs: "Cadastre o custo dos SKUs vinculados em Estoque.",
  shipping: "O frete do vendedor entra na próxima sincronização do envio.",
  unmapped: "Vincule os itens a um SKU para calcular o custo.",
};

function ProfitCard({
  profit,
  provider,
}: {
  provider: string;
  profit: {
    included: boolean;
    source?: "estimated" | "billed";
    revenue: string;
    fees: string | null;
    cogs: string | null;
    shipping: string | null;
    other?: string;
    refund?: string;
    profit: string | null;
    margin: number | null;
    estimatedFees?: string | null;
    estimatedShipping?: string | null;
    delta?: string | null;
    incomplete: string[];
  };
}) {
  const billed = profit.source === "billed";
  const rows = [
    { label: "Receita", value: profit.revenue },
    { label: "Taxa de venda", value: profit.fees },
    { label: "Custo dos SKUs", value: profit.cogs },
    { label: "Frete (vendedor)", value: profit.shipping },
    ...(profit.other && Number(profit.other) !== 0
      ? [{ label: "Outras cobranças", value: profit.other }]
      : []),
    ...(profit.refund && Number(profit.refund) > 0
      ? [{ label: "Reembolso", value: profit.refund }]
      : []),
  ];

  return (
    <Card className="animate-rise [animation-delay:20ms]">
      <CardHeader>
        <CardTitle>Lucro</CardTitle>
        <span className="text-2xs text-ink-muted">
          {billed ? "Conferido no extrato" : "Estimativa do pedido"}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-4 pt-0">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {rows.map((row) => (
            <div key={row.label}>
              <p className="text-2xs text-ink-muted">{row.label}</p>
              <p className="font-mono text-[0.8125rem] text-ink">
                {row.value != null ? formatBRL(row.value) : "—"}
              </p>
            </div>
          ))}
        </div>
        <div className="flex items-baseline justify-between border-t border-line pt-3">
          <span className="text-2xs text-ink-muted">
            {profit.included
              ? "Lucro líquido"
              : "Pedido ainda não realizado — lucro estimado só após o pagamento"}
          </span>
          <span className="font-mono text-[0.8125rem] font-semibold text-ink">
            {profit.profit != null
              ? `${formatBRL(profit.profit)} · ${formatPercent(profit.margin)}`
              : "—"}
          </span>
        </div>
        {billed && profit.delta != null && Number(profit.delta) !== 0 && (
          <p className="text-2xs text-ink-muted">
            A estimativa era{" "}
            {formatBRL(
              (Number(profit.estimatedFees ?? 0) +
                Number(profit.estimatedShipping ?? 0)).toFixed(2),
            )}
            {" "}
            (diferença {formatBRL(profit.delta)}).
          </p>
        )}
        {profit.incomplete.length > 0 && (
          <p className="text-2xs text-ink-muted">
            {profit.incomplete
              .map((key) =>
                key === "fees" ? channelCopy(provider).profitHint : (INCOMPLETE_HINT[key] ?? key),
              )
              .join(" ")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function OrderClaimBody({
  claimId,
  canReply,
  writesEnabled,
  onSent,
}: {
  claimId: string;
  canReply: boolean;
  writesEnabled: boolean;
  onSent: () => void;
}) {
  const detail = useQuery({
    queryKey: ["claims", claimId],
    queryFn: async () => unwrap(await api.claims({ id: claimId }).get()),
  });

  if (detail.isLoading || !detail.data || !("claim" in detail.data)) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4 text-ink-muted" />
      </div>
    );
  }

  const stockReturned =
    "stockReturned" in detail.data && Boolean(detail.data.stockReturned);

  return (
    <div className="flex flex-col gap-3">
      <ClaimReturnButton
        claimId={claimId}
        hasReturn={Boolean(detail.data.claim.hasReturn)}
        stockReturned={stockReturned}
        onDone={onSent}
      />
      <ClaimThread
        claimId={claimId}
        messages={detail.data.messages}
        canReply={
          "canReply" in detail.data
            ? Boolean(detail.data.canReply)
            : canReply
        }
        writesEnabled={writesEnabled}
        vars={replyVarsFromOrder(detail.data.order ?? {})}
        onSent={onSent}
      />
    </div>
  );
}

function ShippingCard({
  orderId,
  order,
}: {
  orderId: string;
  order: {
    externalShipmentId: string | null;
    shippingStatus: string | null;
    shippingSubstatus: string | null;
    trackingNumber: string | null;
    trackingMethod: string | null;
    estimatedDeliveryAt: string | Date | null;
    shippingTimeline: { status: string; substatus: string | null; at: string }[] | null;
  };
}) {
  const toast = useToast();
  const [showHistory, setShowHistory] = useState(false);
  const timeline = order.shippingTimeline ?? [];
  const canPrintLabel =
    order.shippingStatus === "ready_to_ship" && Boolean(order.externalShipmentId);

  const downloadLabel = useMutation({
    mutationFn: (format: "pdf" | "zpl") =>
      downloadAuthenticated(
        `/orders/labels?orderIds=${orderId}&format=${format}`,
        format === "zpl" ? "etiqueta-zpl.zip" : "etiqueta.pdf",
      ),
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível baixar a etiqueta",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      }),
  });

  return (
    <Card className="animate-rise [animation-delay:40ms]">
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5">
          <Truck className="size-3.5 text-ink-muted" />
          Envio
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {order.externalShipmentId || order.shippingStatus ? (
          <>
            <DetailRow
              label="Status"
              value={
                order.shippingStatus
                  ? (SHIPPING_STATUS_LABELS[order.shippingStatus] ??
                    order.shippingStatus)
                  : "Aguardando atualização"
              }
            />
            {order.shippingSubstatus && (
              <DetailRow label="Detalhe" value={order.shippingSubstatus} />
            )}
            {order.trackingNumber && (
              <DetailRow
                label="Rastreio"
                value={order.trackingNumber}
                mono
                copy
              />
            )}
            {order.trackingMethod && (
              <DetailRow label="Transportadora" value={order.trackingMethod} />
            )}
            {order.estimatedDeliveryAt &&
              order.shippingStatus !== "delivered" && (
                <DetailRow
                  label="Previsão"
                  value={formatDate(order.estimatedDeliveryAt)}
                />
              )}
            {order.externalShipmentId && (
              <DetailRow
                label="Envio"
                value={`#${order.externalShipmentId}`}
                mono
              />
            )}
            {canPrintLabel && (
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      variant="secondary"
                      size="sm"
                      className="self-start"
                      loading={downloadLabel.isPending}
                    >
                      <Download />
                      Baixar etiqueta
                      <ChevronDown />
                    </Button>
                  }
                />
                <MenuContent align="start">
                  <MenuItem onClick={() => downloadLabel.mutate("pdf")}>
                    <FileText />
                    PDF — impressora comum
                  </MenuItem>
                  <MenuItem onClick={() => downloadLabel.mutate("zpl")}>
                    <Printer />
                    ZPL — impressora térmica
                  </MenuItem>
                </MenuContent>
              </Menu>
            )}

            {timeline.length > 0 && (
              <>
                <Separator />
                <button
                  type="button"
                  onClick={() => setShowHistory((open) => !open)}
                  className={cn(
                    "flex items-center gap-1 self-start text-2xs font-medium text-ink-secondary",
                    "transition-colors duration-100 hover:text-ink",
                  )}
                >
                  Histórico ({timeline.length})
                  <ChevronDown
                    className={cn(
                      "size-3 transition-transform duration-200 ease-swift",
                      showHistory && "rotate-180",
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-200 ease-swift",
                    showHistory ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <ol className="flex flex-col gap-0 overflow-hidden">
                    {timeline.map((step, index) => (
                      <li key={`${step.status}-${step.at}`} className="flex gap-2.5">
                        <div className="flex w-3 flex-col items-center">
                          <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-ember" />
                          {index < timeline.length - 1 && (
                            <span className="w-px flex-1 bg-line" />
                          )}
                        </div>
                        <div className="pb-3 last:pb-0">
                          <p className="text-[0.8125rem] text-ink">
                            {SHIPPING_STATUS_LABELS[step.status] ?? step.status}
                          </p>
                          <p className="text-2xs text-ink-muted">
                            {formatDateTime(step.at)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </>
        ) : (
          <p className="text-2xs leading-relaxed text-ink-muted">
            Ainda sem dados de envio. Eles entram pelo webhook de shipments ou
            na próxima sincronização do pedido.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function invoiceStatusLabel(status: string | null | undefined, error: string | null | undefined) {
  if (status === "sent") return "Enviada à Shopee";
  if (status === "failed") return `Falhou: ${error || "a Shopee recusou o XML"}`;
  if (status === "pending") return "Pendente";
  return null;
}

function InvoiceCard({
  orderId,
  invoices,
  provider,
}: {
  orderId: string;
  provider: string;
  invoices: Array<{
    id: string;
    documentType: string;
    number: string | null;
    series: string | null;
    accessKey: string | null;
    amount: string | null;
    issuedAt: string | Date | null;
    hasXml: boolean;
    source?: string | null;
    uploadStatus?: string | null;
    uploadError?: string | null;
  }>;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const upload = useMutation({
    mutationFn: async (xml: string) =>
      unwrap(await api.orders({ id: orderId }).invoices.post({ xml })),
    onSuccess: () => {
      toast.add({ type: "success", title: "NF-e na fila de envio" });
      void queryClient.invalidateQueries({ queryKey: ["orders", orderId] });
    },
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível enviar a NF-e",
        description: error instanceof Error ? error.message : "Tente de novo.",
      }),
  });
  const downloadXml = useMutation({
    mutationFn: (invoiceId: string) =>
      downloadAuthenticated(
        `/orders/${orderId}/invoices/${invoiceId}/xml`,
        "nfe.xml",
      ),
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível baixar o XML",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      }),
  });

  return (
    <Card className="animate-rise [animation-delay:80ms]">
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5">
          <FileText className="size-3.5 text-ink-muted" />
          Nota fiscal
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {invoices.length > 0 ? (
          invoices.map((invoice, index) => (
            <div key={invoice.id} className="flex flex-col gap-3">
              {index > 0 && <Separator />}
              <DetailRow
                label="Documento"
                value={
                  invoice.documentType === "dce"
                    ? "DC-e"
                    : invoice.documentType === "nfe"
                      ? "NF-e"
                      : invoice.documentType
                }
              />
              <DetailRow
                label="Número"
                value={
                  invoice.number
                    ? invoice.series
                      ? `${invoice.number} / ${invoice.series}`
                      : invoice.number
                    : "—"
                }
              />
              {invoice.accessKey && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-2xs text-ink-muted">Chave</span>
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="font-mono text-2xs text-ink" title={invoice.accessKey}>
                      {truncateMiddle(invoice.accessKey)}
                    </span>
                    <CopyButton
                      value={invoice.accessKey}
                      label="Copiar chave de acesso"
                    />
                  </span>
                </div>
              )}
              {invoice.amount && (
                <DetailRow label="Valor" value={formatBRL(invoice.amount)} />
              )}
              {invoice.issuedAt && (
                <DetailRow
                  label="Emissão"
                  value={formatDateTime(invoice.issuedAt)}
                />
              )}
              {invoiceStatusLabel(invoice.uploadStatus, invoice.uploadError) ? (
                <DetailRow
                  label="Envio"
                  value={
                    invoiceStatusLabel(invoice.uploadStatus, invoice.uploadError) ??
                    "—"
                  }
                />
              ) : null}
              {invoice.hasXml ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  loading={downloadXml.isPending}
                  onClick={() => downloadXml.mutate(invoice.id)}
                >
                  <Download />
                  Baixar XML
                </Button>
              ) : (
                <p className="text-2xs leading-relaxed text-ink-muted">
                  {channelCopy(provider).invoiceEmpty}
                </p>
              )}
            </div>
          ))
        ) : null}
        {provider === "shopee" && invoices.length > 0 ? (
          <label className="flex cursor-pointer flex-col items-center gap-1 rounded-control border border-dashed border-line px-3 py-3 text-center text-2xs text-ink-muted transition-colors duration-150 hover:border-ink-faint hover:text-ink">
            <span>{upload.isPending ? "Lendo o XML…" : "Enviar outro XML"}</span>
            <input
              type="file"
              accept=".xml,text/xml,application/xml"
              className="sr-only"
              disabled={upload.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                void file.text().then((xml) => upload.mutate(xml));
              }}
            />
          </label>
        ) : null}
        {invoices.length === 0 ? (
          provider === "shopee" ? (
            <div className="flex flex-col gap-3">
              <ChannelNotice provider={provider}>
                {channelCopy(provider).invoiceEmpty}
              </ChannelNotice>
              <label className="flex cursor-pointer flex-col items-center gap-1 rounded-control border border-dashed border-line px-3 py-4 text-center text-2xs text-ink-muted transition-colors duration-150 hover:border-ink-faint hover:text-ink">
                <span>{upload.isPending ? "Lendo o XML…" : "Solte o XML da NF-e ou clique para escolher"}</span>
                <input
                  type="file"
                  accept=".xml,text/xml,application/xml"
                  className="sr-only"
                  disabled={upload.isPending}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (!file) return;
                    void file.text().then((xml) => upload.mutate(xml));
                  }}
                />
              </label>
            </div>
          ) : (
            <p className="text-2xs leading-relaxed text-ink-muted">
              {channelCopy(provider).invoiceEmpty}
            </p>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

function DetailRow({
  label,
  value,
  strong,
  mono,
  copy,
}: {
  label: string;
  value: string;
  strong?: boolean;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-2xs text-ink-muted">{label}</span>
      <span
        className={cn(
          "flex min-w-0 items-center gap-1.5",
          strong
            ? "text-[0.8125rem] font-semibold text-ink"
            : mono
              ? "font-mono text-2xs text-ink"
              : "text-[0.8125rem] text-ink",
        )}
      >
        <span className={cn(mono && "truncate")} title={mono ? value : undefined}>
          {value}
        </span>
        {copy && <CopyButton value={value} label={`Copiar ${label.toLowerCase()}`} />}
      </span>
    </div>
  );
}
